import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { prepareSplitHOC } from "../../../../../hoc/prepareSplitHOC";
import { calcRRMarks } from "../../../../../graphics/graph-funcs";

import { measurerGraphMainAdapter } from "./measurer-graph-main-adapter";

import VisualizatorSettings from "./VisualizatorSettings.json";

const graphWrapStyle = {
    height: "100%",
    width: "100%",
    position: "relative",
    backgroundColor: "white"
};

const MeasurerGraphMain = forwardRef((props, ref) => {
    const isMounted = useRef(false);
    const canvasWrap = useRef(null);
    const graphAdapter = useRef(null);
    const selectRectsAreVisible = useRef(true);
    const { signalData, setMainGraphWidth, fd, refreshFlag, areaWidth, areaForceUpdate, userSweepUnits } = props;

    useEffect(() => {
        const mount = async () => {
            const { onDragMove, onAreaWidthChange, signalLength } = props;
            graphAdapter.current = new measurerGraphMainAdapter({
                fd: fd,
                updateTabelValuesCallback: onDragMove,
                updateCursorAreaCallback: onAreaWidthChange,
                canvasRef: canvasWrap.current,
                visualSettings: VisualizatorSettings,
                signalLength: signalLength
            });

            graphAdapter.current.changeDefaultScaleSettings(props.scale);
            graphAdapter.current.changeDefaultFrequencySettungs(fd);
            await graphAdapter.current.initializeGraph();
            await graphAdapter.current.setupMeasurers();
            await graphAdapter.current.setupMarks();
            await graphAdapter.current.setupRectangles();
            graphAdapter.current.setUserSweepUnits(userSweepUnits);

            const graphWidth = graphAdapter.current.getWidthInPoints(fd);
            setMainGraphWidth(graphWidth);
            graphAdapter.current.onResize(onResize);

            isMounted.current = true;
        };

        mount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const update = async () => {
            if (signalData.length) {
                await updateGraph();
            }
        };

        update();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signalData, refreshFlag]);

    useEffect(() => {
        if (isMounted.current) {
            if (areaForceUpdate) {
                graphAdapter.current.updateCursorArea(areaWidth);
                graphAdapter.current.updateTabelValues();
            }
        }
    }, [areaWidth, areaForceUpdate]);

    useEffect(() => {
        if (isMounted.current) {
            graphAdapter.current.setUserSweepUnits(userSweepUnits);
        }
    }, [userSweepUnits]);

    const updateGraph = async () => {
        await Promise.all([graphAdapter.current.clearMarks(), graphAdapter.current.clearGraph()]);
        await graphAdapter.current.plotData(signalData);
        await Promise.all([updateMarks(), updateSelectRectangles()]);
        graphAdapter.current.updateTabelValues();
    };

    const updateMarks = async () => {
        const { peaks } = props;
        const { marks: marksRR } = calcRRMarks(peaks.current[0], fd, 5);

        const convertedMarks = [
            ...graphAdapter.current.convertQRSMarksToStructure(peaks.current.flat()),
            ...graphAdapter.current.convertRRMarksToStructure(marksRR)
        ];
        await graphAdapter.current.plotMarks(convertedMarks);
    };

    const updateSelectRectangles = () => {
        const { Q, S, signalStart } = props;

        const graphWIdth = graphAdapter.current.getWidthInPoints(fd);

        let start = Q - 0.25 * fd - signalStart; // Точка Q - 0.25 секунды
        let end = start + S + 0.25 * 2 * fd; // Точка S + 0.25 секунды
        start = Math.max(0, start);
        end = Math.min(graphWIdth, end);

        const width = end - start + 1;

        if (width <= 0) {
            if (selectRectsAreVisible.current) {
                graphAdapter.current.hideSelectRectangles(false);
                selectRectsAreVisible.current = false;
            }
        } else {
            if (!selectRectsAreVisible.current) {
                graphAdapter.current.hideSelectRectangles(true);
                selectRectsAreVisible.current = true;
            }
            graphAdapter.current.moveSelectRectangles(start, width);
        }
    };

    const onResize = async () => {
        const { setRefreshFlag } = props;

        setMainGraphWidth(graphAdapter.current.getWidthInPoints(fd));
        setRefreshFlag(prev => prev + 1);
    };

    const changeScaleY = async type => {
        const index = await graphAdapter.current.changeScaleY(type);
        await graphAdapter.current.clearMarks();
        await updateMarks();

        return index;
    };

    const changeScaleX = type => {
        const index = graphAdapter.current.changeScaleX(type);
        const graphWidth = graphAdapter.current.getWidthInPoints(fd);
        setMainGraphWidth(graphWidth);

        return [index, graphWidth];
    };

    useImperativeHandle(ref, () => ({
        changeScaleX,
        changeScaleY
    }));

    return <div className="canvas-wrap" style={graphWrapStyle} ref={canvasWrap} />;
});

export default prepareSplitHOC(MeasurerGraphMain);
